import { useFlags } from '@atlaskit/flag';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useEffect, useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { SettingsVehicleChecksApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { ISettingsVehicleCheckDao, ISettingsVehicleCheckDto } from 'core/api/types/settings-vehicle-checks.interface';
import { AddEditSettingsVehicleCheckFormFields } from 'core/config/form-fields';
import { IAddEditSettingsVehicleCheckFormOutput } from '../types';
import { Frequency, getFrequencyNameFromKey } from 'core/constants/frequency';

const AddEditSettingsVehicleCheckDialog = ({
  settingsVehicleCheck,
}: {
  settingsVehicleCheck?: ISettingsVehicleCheckDao;
}) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();

  const [formFields, setFormFields] = useState<ISharedFormField[]>(AddEditSettingsVehicleCheckFormFields);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const getDefaultValue = (settingsVehicleCheck: ISettingsVehicleCheckDao, key: string) => {
    switch (key) {
      case 'requireComment':
        return settingsVehicleCheck?.requireComment.map((value) => ({
          value: value,
          label: settingsVehicleCheck?.options.find((option) => option.value === value)?.label,
        }));
      case 'frequency':
        return settingsVehicleCheck?.frequency.map((value) => ({
          value: value,
          label: getFrequencyNameFromKey(value as Frequency),
        }));
      default:
        return settingsVehicleCheck[key as keyof ISettingsVehicleCheckDao];
    }
  };

  useEffect(() => {
    const fields = AddEditSettingsVehicleCheckFormFields.map((field) => {
      if (
        (settingsVehicleCheck?.name === 'Mileage' ||
          settingsVehicleCheck?.name === 'Tyres' ||
          settingsVehicleCheck?.name === 'Damage') &&
        (field.key === 'name' || field.key === 'options' || field.key === 'requireComment')
      ) {
        return {
          ...field,
          defaultValue: settingsVehicleCheck ? getDefaultValue(settingsVehicleCheck, field.key) : field.defaultValue,
          isDisabled: true,
        };
      }

      if (field.key === 'requireComment') {
        return {
          ...field,
          defaultValue: settingsVehicleCheck ? getDefaultValue(settingsVehicleCheck, field.key) : field.defaultValue,
          isDisabled: !settingsVehicleCheck?.options.length,
          options: settingsVehicleCheck?.options,
        };
      }

      return {
        ...field,
        defaultValue: settingsVehicleCheck ? getDefaultValue(settingsVehicleCheck, field.key) : field.defaultValue,
      };
    });

    setFormFields(fields as ISharedFormField[]);
  }, [settingsVehicleCheck]);

  const addEditSettingsVehicleCheck = async (data: IAddEditSettingsVehicleCheckFormOutput) => {
    setFormSubmitting(true);

    const payload: ISettingsVehicleCheckDto = {
      name: data.name,
      active: data.active,
      options: data.options,
      requireComment: data.requireComment.map(({ value }) => value),
      frequency: data.frequency.map(({ value }) => value),
    };

    try {
      if (settingsVehicleCheck && settingsVehicleCheck.uid !== '0') {
        await SettingsVehicleChecksApiService.update(settingsVehicleCheck.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await SettingsVehicleChecksApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }
      showSuccessFlag(
        settingsVehicleCheck ? 'Settings vehicle check updated' : 'Settings vehicle check created',
        `The settings vehicle check has been ${settingsVehicleCheck ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        settingsVehicleCheck ? 'Settings vehicle check update failed' : 'Settings vehicle check creation failed',
        `Unable to ${settingsVehicleCheck ? 'update' : 'create'} a settings vehicle check, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const handleFormChange = (data: any) => {
    if (data.options) {
      setFormFields(
        formFields.map((field) =>
          field.key === 'requireComment' ? { ...field, isDisabled: !data.options.length, options: data.options } : field
        )
      );
    }
  };

  const customContent = () => {
    return (
      <>
        <SharedForm
          className='p-4 overflow-y-auto'
          onSubmit={addEditSettingsVehicleCheck}
          onChange={handleFormChange}
          fields={formFields as ISharedFormField[]}
          buttonLabel='Submit'
          loading={formSubmitting}
          cancelButton={cancelButton}
        />
      </>
    );
  };

  return (
    <SharedDialogBase
      title={settingsVehicleCheck ? 'Update settings vehicle check' : 'Add settings vehicle check'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditSettingsVehicleCheckDialog;
